import React, { useCallback, useMemo, useState } from 'react';
import CategoryFilterPopover from './CategoryFilterPopover';
import CategoryRenameDialog from './CategoryRenameDialog';
import CategoryFilterChips from './CategoryFilterChips';
import {
    applyCategoryRenameToFilter,
    describeCategoryFilter,
    isCategoryFilterActive,
    rowMatchesCategoryFilters,
} from './categoryFilter';
import {
    MAX_ATTRIBUTE_CATEGORIES,
    categoryDisplay,
    categoryFacets,
    categoryKey,
    planReplaceCategory,
} from './categoryTools';

const EMPTY_VALUES = Object.freeze([]);

function securities(count) {
    return `${count.toLocaleString()} ${count === 1 ? 'security' : 'securities'}`;
}

// categoryKey per distinct text: a column repeats a few dozen values over
// thousands of rows.
function cachedCategoryKey() {
    const keys = new Map();
    return (value) => {
        const text = value == null ? '' : String(value);
        let key = keys.get(text);
        if (key === undefined) {
            key = categoryKey(text);
            keys.set(text, key);
        }
        return key;
    };
}

function withoutKey(object, key) {
    if (!object || !Object.prototype.hasOwnProperty.call(object, key)) return object;
    const next = { ...object };
    delete next[key];
    return next;
}

/**
 * The columns Save must not write: more than MAX_ATTRIBUTE_CATEGORIES
 * categories once the drafts are applied (`readValue`), at least one of them
 * new. Like the server, a column whose saved values already hold more may
 * keep or reduce them. Returns [{ key, label, count }].
 */
export function categoryLimitViolations({ columns, rows, readValue, readSavedValue }) {
    const violations = [];
    columns.forEach((column) => {
        const keyOf = cachedCategoryKey();
        const keys = new Set();
        rows.forEach((row) => {
            const key = keyOf(readValue(row, column.key));
            if (key) keys.add(key);
        });
        if (keys.size <= MAX_ATTRIBUTE_CATEGORIES) return;
        const savedKeys = new Set(rows.map((row) => keyOf(readSavedValue(row, column.key))));
        if ([...keys].every((key) => savedKeys.has(key))) return;
        violations.push({ key: column.key, label: column.label, count: keys.size });
    });
    return violations;
}

/** Why Save is blocked, naming each column and its count; '' when it is not. */
export function describeCategoryLimitViolations(violations) {
    if (!violations.length) return '';
    const [first, ...rest] = violations;
    const parts = [
        `${first.label} has ${first.count} categories`,
        ...rest.map((violation) => `${violation.label} has ${violation.count}`),
    ];
    const list = parts.length > 1
        ? `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`
        : parts[0];
    return `${list}. A column can hold at most ${MAX_ATTRIBUTE_CATEGORIES} categories, so merge or clear some before saving.`;
}

/**
 * Category tools for the Watchlist grid's attribute columns: Excel-style
 * column filters on saved values, the filter popover with Rename…, the rename
 * dialog, toolbar chips for active filters and the category-limit Save guard.
 *
 * - columns: [{ key, field, label, editable }] in display order; `key` names
 *   the column in `filters` and the read functions, `field` is its grid
 *   column id, and `editable` offers Rename…
 * - filters / onFiltersChange: the owner's session state,
 *   { [key]: { keys, blank } }; onFiltersChange takes a state updater
 * - rows: every row (rename and the guard cover all of them); scopeRows: the
 *   rows passing the quick filter
 * - readSavedValue(row, key) / readValue(row, key): saved text, and the text
 *   Save would write (drafts included)
 * - isEditable(rowId, field), getRowId(row)
 * - applyEdits(edits, label, describe): the grid's commands.applyEdits, so a
 *   rename is one undo step with the Undo toast
 *
 * Returns visibleRows (scopeRows passing the column filters), headerContext
 * (for CategoryColumnsContext), chips and dialogs (elements to render),
 * getEditorOptions(key) (the column's categories, one spelling each),
 * limitViolations and saveBlockedMessage.
 */
export default function useCategoryColumnTools({
    columns,
    filters,
    onFiltersChange,
    rows,
    scopeRows,
    getRowId,
    readSavedValue,
    readValue,
    isEditable,
    applyEdits,
}) {
    // Kept after closing (open: false) so the popover and dialog keep their
    // content through their closing transitions.
    const [filterMenu, setFilterMenu] = useState(null);
    const [rename, setRename] = useState(null);

    const columnsByKey = useMemo(
        () => new Map(columns.map((column) => [column.key, column])),
        [columns],
    );
    // Only filters of columns that still exist and leave something out.
    const activeFilters = useMemo(() => {
        const active = {};
        columns.forEach((column) => {
            const filter = filters?.[column.key];
            if (isCategoryFilterActive(filter)) active[column.key] = filter;
        });
        return active;
    }, [columns, filters]);

    const visibleRows = useMemo(() => (
        Object.keys(activeFilters).length === 0
            ? scopeRows
            : scopeRows.filter((row) => rowMatchesCategoryFilters(
                (key) => readSavedValue(row, key),
                activeFilters,
            ))
    ), [activeFilters, readSavedValue, scopeRows]);

    const setColumnFilter = (key, filter) => {
        onFiltersChange((current) => (
            isCategoryFilterActive(filter) ? { ...current, [key]: filter } : withoutKey(current, key)
        ));
    };
    const openFilter = useCallback((key, anchorEl) => {
        setFilterMenu({ key, anchorEl, open: true });
    }, []);

    // The open filter lists the saved values of the rows the quick filter
    // and the other columns' filters keep, and unticks against every saved
    // value in the column.
    const menuColumn = filterMenu ? columnsByKey.get(filterMenu.key) : null;
    const menuValues = useMemo(() => {
        if (!menuColumn) return null;
        const otherFilters = withoutKey(activeFilters, menuColumn.key);
        return {
            values: scopeRows
                .filter((row) => rowMatchesCategoryFilters((key) => readSavedValue(row, key), otherFilters))
                .map((row) => readSavedValue(row, menuColumn.key)),
            allValues: rows.map((row) => readSavedValue(row, menuColumn.key)),
        };
    }, [activeFilters, menuColumn, readSavedValue, rows, scopeRows]);

    const chipItems = useMemo(() => columns
        .filter((column) => activeFilters[column.key])
        .map((column) => ({
            key: column.key,
            label: column.label,
            summary: describeCategoryFilter(
                activeFilters[column.key],
                categoryFacets(rows.map((row) => readSavedValue(row, column.key))),
            ),
        })), [activeFilters, columns, readSavedValue, rows]);

    const limitViolations = useMemo(
        () => categoryLimitViolations({ columns, rows, readValue, readSavedValue }),
        [columns, readSavedValue, readValue, rows],
    );
    const openKey = filterMenu?.open ? filterMenu.key : null;
    const headerContext = useMemo(() => ({
        filteredKeys: new Set(Object.keys(activeFilters)),
        openKey,
        overLimitCounts: Object.fromEntries(limitViolations.map((violation) => [violation.key, violation.count])),
        openFilter,
    }), [activeFilters, limitViolations, openFilter, openKey]);

    const getEditorOptions = useMemo(() => {
        const cache = new Map();
        return (key) => {
            if (!cache.has(key)) {
                cache.set(key, categoryFacets(rows.map((row) => readValue(row, key)))
                    .categories.map((category) => category.value));
            }
            return cache.get(key);
        };
    }, [readValue, rows]);

    // Rename covers every row, including rows the filters hide; the dialog
    // says how many will change.
    const planRename = (column, fromValue, toValue) => {
        const rowsById = new Map(rows.map((row) => [getRowId(row), row]));
        return planReplaceCategory({
            rowIds: [...rowsById.keys()],
            columnId: column.field,
            readValue: (rowId) => readValue(rowsById.get(rowId), column.key),
            isEditable,
            fromValue,
            toValue,
        });
    };
    const requestRename = (column, fromValue) => setRename({
        key: column.key,
        fromValue,
        rowCount: planRename(column, fromValue, fromValue).length,
        existingValues: categoryFacets(rows.map((row) => readValue(row, column.key)))
            .categories.map((category) => category.value),
        open: true,
    });
    const renameColumn = rename ? columnsByKey.get(rename.key) : null;
    // The rename dialog warns exactly when Save would refuse the result.
    const categoryCountAfter = (toValue) => {
        const fromKey = categoryKey(rename.fromValue);
        const [violation] = categoryLimitViolations({
            columns: [renameColumn],
            rows,
            readValue: (row, key) => {
                const value = readValue(row, key);
                return categoryKey(value) === fromKey && isEditable(getRowId(row), renameColumn.field)
                    ? toValue
                    : value;
            },
            readSavedValue,
        });
        return violation ? violation.count : 0;
    };
    const confirmRename = (toValue) => {
        const { key, fromValue, existingValues } = rename;
        setRename((current) => ({ ...current, open: false }));
        const column = columnsByKey.get(key);
        if (!column) return;
        const from = categoryDisplay(fromValue);
        const to = categoryDisplay(toValue);
        const toKey = categoryKey(to);
        const merging = toKey !== categoryKey(from)
            && existingValues.some((value) => categoryKey(value) === toKey);
        const action = applyEdits(planRename(column, fromValue, toValue), 'Rename', (count) => {
            if (!toKey) return `Cleared ${from} on ${securities(count)}`;
            return merging
                ? `Merged ${from} into ${to} on ${securities(count)}`
                : `Renamed ${from} to ${to} on ${securities(count)}`;
        });
        // Filters match saved values: tick the new name too, so the renamed
        // rows stay in view once the rename is saved.
        if (action) {
            onFiltersChange((current) => {
                const filter = current?.[key];
                const next = applyCategoryRenameToFilter(filter, fromValue, toValue);
                return next === filter ? current : { ...current, [key]: next };
            });
        }
    };

    const chips = (
        <CategoryFilterChips
            items={chipItems}
            onOpen={openFilter}
            onClear={(key) => onFiltersChange((current) => withoutKey(current, key))}
            onClearAll={() => onFiltersChange({})}
        />
    );
    const dialogs = (
        <>
            <CategoryFilterPopover
                open={Boolean(filterMenu?.open && menuColumn)}
                anchorEl={filterMenu?.anchorEl ?? null}
                columnLabel={menuColumn?.label ?? ''}
                values={menuValues?.values ?? EMPTY_VALUES}
                allValues={menuValues?.allValues}
                filter={menuColumn ? activeFilters[menuColumn.key] ?? null : null}
                onChange={(next) => setColumnFilter(menuColumn.key, next)}
                onClose={() => setFilterMenu((current) => current && { ...current, open: false })}
                onRenameRequest={menuColumn?.editable
                    ? (category) => requestRename(menuColumn, category.value)
                    : undefined}
            />
            {renameColumn && (
                <CategoryRenameDialog
                    open={rename.open}
                    fromValue={rename.fromValue}
                    rowCount={rename.rowCount}
                    existingValues={rename.existingValues}
                    columnLabel={renameColumn.label}
                    categoryCountAfter={rename.open ? categoryCountAfter : undefined}
                    onConfirm={confirmRename}
                    onCancel={() => setRename((current) => ({ ...current, open: false }))}
                />
            )}
        </>
    );

    return {
        visibleRows,
        headerContext,
        chips,
        dialogs,
        getEditorOptions,
        limitViolations,
        saveBlockedMessage: describeCategoryLimitViolations(limitViolations),
    };
}
